import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useRegister } from '../hooks/useRegister';
import authService from '../services/authService';
import recaptchaService from '../utils/recaptchaService';
import apiService from '../services/apiService';
import Header from '../components/Header';
import './css/Register.css';

function Register() {
  const navigate = useNavigate();
  const { register, isLoading } = useRegister();
  
  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [department, setDepartment] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);

  // Institute selection state
  const [belongsToInstitute, setBelongsToInstitute] = useState(false);
  const [instituteQuery, setInstituteQuery] = useState('');
  const [instituteResults, setInstituteResults] = useState([]);
  const [selectedInstitute, setSelectedInstitute] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  // reCAPTCHA initialization
  useEffect(() => {
    const initRecaptcha = async () => {
      console.log('Initializing reCAPTCHA v3...');
      const ready = await recaptchaService.init();
      if (ready) {
        console.log('✓ reCAPTCHA initialized successfully');
      } else {
        console.error('✗ Failed to initialize reCAPTCHA');
      }
    };

    initRecaptcha();
    return () => recaptchaService.cleanup();
  }, []);

  // Debounce utility function
  const debounce = (func, delay) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), delay);
    };
  };

  // Search institutes with debouncing
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const searchInstitutes = useCallback(
    debounce(async (query) => {
      if (query.length < 3) {
        setInstituteResults([]);
        return;
      }
      
      setIsSearching(true);
      try {
        const data = await apiService.searchInstitutes(query);
        setInstituteResults(data);
      } catch (err) {
        console.error("Error searching institutes:", err);
        setInstituteResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 500),
    []
  );

  // Effect to handle institute search
  useEffect(() => {
    // Prevent re-search if query matches selected institute's name
    if (selectedInstitute && instituteQuery === selectedInstitute.name) {
      setInstituteResults([]);
      return;
    }

    if (instituteQuery) {
      searchInstitutes(instituteQuery);
    } else {
      setInstituteResults([]);
    }
  }, [instituteQuery, searchInstitutes, selectedInstitute]);

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setEmailVerificationSent(false);

    // Validation
    if (!firstName || !lastName || !email || !password || !confirmPassword) {
      setError("Please fill in all required fields (First Name, Last Name, Email, Password).");
      return;
    }

    if (firstName.length > 150) {
      setError("First name must not exceed 150 characters.");
      return;
    }

    if (lastName.length > 150) {
      setError("Last name must not exceed 150 characters.");
      return;
    }

    if (mobileNumber && mobileNumber.length > 20) {
      setError("Mobile number must not exceed 20 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }

    try {
      // Execute reCAPTCHA v3
      console.log('Executing reCAPTCHA for registration action...');
      const recaptchaToken = await recaptchaService.executeToken('register');

      if (!recaptchaToken) {
        console.error('✗ Failed to obtain reCAPTCHA token');
        setError("Security verification failed. Please refresh the page and try again.");
        return;
      }

      console.log('✓ reCAPTCHA token obtained, proceeding with registration');

      const result = await register(
        email,
        password,
        confirmPassword,
        firstName,
        lastName,
        department || null,
        selectedInstitute?.id,
        mobileNumber || null,
        recaptchaToken
      );
      
      if (result.success) {
        const responseData = result.data || {};
        setRegisteredEmail(responseData.email || email);
        
        // Check if email verification is required
        if (responseData.verification_email_sent || responseData.email_verified === false) {
          setEmailVerificationSent(true);
          setSuccess(
            responseData.message || 
            `Registration successful! A verification email has been sent to ${responseData.email || email}. Please verify your email before logging in.`
          );
          // Redirect to verify email page
          setTimeout(() => {
            navigate('/verify-email');
          }, 2000);
        } else {
          // If no email verification needed (e.g., @inpods.com user), auto login and navigate to courses
          setSuccess(result.message || "Registration successful!");
          setTimeout(() => {
            const selectedRole = localStorage.getItem('users-selected-role') || 'instructor';
            const redirectPath = authService.getRoleRedirectPath(selectedRole);
            navigate(redirectPath);
          }, 2000);
        }
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError("An unexpected error occurred");
      console.error(err);
    }
  };

  const handleInstituteSelect = (institute) => {
    setSelectedInstitute(institute);
    setInstituteQuery(institute.name);
    setInstituteResults([]);
  };

  const handleBelongsToInstituteChange = (e) => {
    const checked = e.target.checked;
    setBelongsToInstitute(checked);
    
    if (!checked) {
      setSelectedInstitute(null);
      setInstituteQuery('');
    }
  };

  return (
    <div className="app-container">
      <Header />
      
      <div className="auth-container register-container">
        <h1 className="register-title">Register</h1>
        
        {error && (
          <div className="auth-error">
            {error}
          </div>
        )}
        
        {success && (
          <div className="auth-success">
            <div className="success-message">{success}</div>
            {emailVerificationSent && registeredEmail && (
              <div className="verification-notice">
                <p className="verification-title">📧 Verification Email Sent</p>
                <p>We've sent a verification link to <strong>{registeredEmail}</strong></p>
                <p>Please check your email (including spam folder) and click the verification link before logging in.</p>
              </div>
            )}
          </div>
        )}
        
        <form onSubmit={handleRegister} className="register-form">
          {/* First Name and Last Name Row */}
          <div className="form-row">
            <div className="form-group">
              <input
                id="firstName"
                type="text"
                className="form-control"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                maxLength="150"
                required
                disabled={isLoading || emailVerificationSent}
                placeholder="First Name"
              />
            </div>
            
            <div className="form-group">
              <input
                id="lastName"
                type="text"
                className="form-control"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                maxLength="150"
                required
                disabled={isLoading || emailVerificationSent}
                placeholder="Last Name"
              />
            </div>
          </div>

          {/* Email and Mobile Number Row */}
          <div className="form-row">
            <div className="form-group">
              <input
                id="email"
                type="email"
                className="form-control"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading || emailVerificationSent}
                placeholder="E-mail"
              />
            </div>
            
            <div className="form-group">
              <input
                id="mobileNumber"
                type="tel"
                className="form-control"
                value={mobileNumber}
                onChange={(e) => setMobileNumber(e.target.value)}
                maxLength="20"
                disabled={isLoading || emailVerificationSent}
                placeholder="Mobile Number"
              />
            </div>
          </div>

          {/* Department Row */}
          <div className="form-row">
            <div className="form-group">
              <input
                id="department"
                type="text"
                className="form-control"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                disabled={isLoading || emailVerificationSent}
                placeholder="Department (Optional)"
              />
            </div>
            <div className="form-group">
              {/* Placeholder to align grid */}
            </div>
          </div>

          {/* Password and Confirm Password Row */}
          <div className="form-row">
            <div className="form-group" style={{ position: 'relative' }}>
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                className="form-control"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading || emailVerificationSent}
                minLength="8"
                placeholder="Password"
                style={{ paddingRight: '2.5rem' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(s => !s)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                tabIndex={-1}
                style={{
                  position: 'absolute', right: '0.6rem', top: '50%',
                  transform: 'translateY(-50%)', background: 'none', border: 'none',
                  cursor: 'pointer', fontSize: '1.1rem', padding: 0, lineHeight: 1,
                  color: '#666',
                }}
              >
                {showPassword ? '🙈' : '👁'}
              </button>
            </div>

            <div className="form-group" style={{ position: 'relative' }}>
              <input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                className="form-control"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={isLoading || emailVerificationSent}
                minLength="8"
                placeholder="Confirm Password"
                style={{ paddingRight: '2.5rem' }}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(s => !s)}
                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                tabIndex={-1}
                style={{
                  position: 'absolute', right: '0.6rem', top: '50%',
                  transform: 'translateY(-50%)', background: 'none', border: 'none',
                  cursor: 'pointer', fontSize: '1.1rem', padding: 0, lineHeight: 1,
                  color: '#666',
                }}
              >
                {showConfirmPassword ? '🙈' : '👁'}
              </button>
            </div>
          </div>
          
          {/* Institute Checkbox - Full Width */}
          <div className="form-row form-row-full">
            <div className="form-group">
              <div className="form-check-label institute-checkbox-container">
                <input
                  id="belongsToInstitute-checkbox"
                  type="checkbox"
                  className="form-check-input institute-checkbox"
                  checked={belongsToInstitute}
                  onChange={handleBelongsToInstituteChange}
                  disabled={isLoading || emailVerificationSent}
                />
                <span className="institute-checkbox-label">
                  I belong to an institute
                </span>
              </div>
            </div>
          </div>

          {/* Institute Search - Full Width */}
          {belongsToInstitute && (
            <div className="form-row form-row-full">
              <div className="form-group institute-search-container">
                <input
                  id="institute-search"
                  type="text"
                  className="form-control institute-search-input"
                  value={instituteQuery}
                  onChange={(e) => setInstituteQuery(e.target.value)}
                  placeholder="Start typing your institute's name..."
                  disabled={isLoading || emailVerificationSent}
                  autoComplete="off"
                />
                
                {isSearching && (
                  <div className="form-text institute-search-loading">
                    Searching...
                  </div>
                )}
                
                {instituteResults.length > 0 && (
                  <ul className="list-group institute-results-list">
                    {instituteResults.map((institute) => (
                      <li
                        key={institute.id}
                        className="list-group-item list-group-item-action institute-result-item"
                        onClick={() => handleInstituteSelect(institute)}
                      >
                        {institute.name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
          
          {/* Submit Button - Full Width */}
          {!emailVerificationSent && (
            <div className="form-row form-row-full">
              <button
                className="btn btn-primary register-submit-button"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? "Registering..." : "Register"}
              </button>
            </div>
          )}
        </form>
        
        <p className="register-login-link">
          {emailVerificationSent ? (
            <>After verifying your email, <Link to="/login">click here to login</Link></>
          ) : (
            <>Already have an account? <Link to="/login">Login</Link></>
          )}
        </p>
      </div>
    </div>
  );
}

export default Register;
